import { useEffect, useMemo, useRef, useState } from 'react';

import {
  extractVariableDefinitions,
  getCurrentFields,
  getCurrentTemplate,
} from '@repo/videa-notes';

import { getNoteSummaryWithoutBlurb } from '../helpers';
import type {
  EditedFields,
  UseNoteManagementProps,
  UseNoteManagementReturn,
} from '../types';

import { useNoteAutoSave } from './useNoteAutoSave';
import { useNoteHandlers } from './useNoteHandlers';
import { useNoteSelection } from './useNoteSelection';
import { useNoteSync } from './useNoteSync';

/**
 * Main orchestration hook for note management functionality.
 *
 * This hook coordinates all aspects of note editing and management:
 * - Manages local UI state (note content, consent blurbs, edited fields, saving state)
 * - Tracks previous values for change detection (via refs)
 * - Handles note selection and fetching full note data when needed
 * - Synchronizes UI state with backend data when notes change
 * - Auto-saves changes after a debounce period
 * - Provides handlers for saving, resetting, and variable value changes
 * - Computes derived values (variable definitions, edit status, PMS writeback status)
 *
 * This is the primary hook that should be used by components that need to manage
 * note editing functionality. It combines useNoteSelection, useNoteSync, useNoteAutoSave,
 * and useNoteHandlers into a single cohesive interface.
 */
export const useNoteManagement = ({
  clinicalNotesData,
  hasWritebackEnabled,
  enableVoiceNotesConsent,
}: UseNoteManagementProps): UseNoteManagementReturn => {
  // Variables
  const [noteContent, setNoteContent] = useState('');
  const [patientConsentBlurb, setPatientConsentBlurb] = useState<
    string | undefined
  >(undefined);
  const [editedFields, setEditedFields] = useState<EditedFields>(null);

  /**
   * Ref tracking current note content, kept in sync with noteContent state.
   * Used by useNoteSync to check for unsaved changes without causing re-renders.
   */
  const noteContentRef = useRef(noteContent);

  /**
   * Stores the last saved content for regular (non-template) notes.
   * Used to detect if the current content differs from what was last saved,
   * preventing unnecessary auto-save operations when content hasn't changed.
   * Updated after each successful save operation.
   */
  const prevContentRef = useRef(noteContent);
  /**
   * Stores the last saved template content for template-based notes.
   * Used to detect if the current template differs from what was last saved,
   * preventing unnecessary auto-save operations when template hasn't changed.
   * Set to `null` for non-template notes. Updated after each successful save operation.
   */
  const prevTemplateRef = useRef<string | null>(null);
  /**
   * Flag to prevent auto-save from triggering during note reset operations.
   * When a note is being reset to its original state, content changes occur
   * that would normally trigger auto-save. This ref prevents those saves from
   * happening until the reset operation completes, avoiding race conditions
   * and unnecessary API calls.
   * Set to `true` when reset starts, `false` when reset completes (success or error).
   */
  const isResettingRef = useRef(false);

  const {
    selectedNote,
    noteWithTemplateData,
    setSelectedNoteId,
    refetchFullNote,
  } = useNoteSelection({ clinicalNotesData });

  // Booleans
  const [isSavingNoteUpdate, setIsSavingNoteUpdate] = useState(false);

  useNoteSync({
    selectedNote,
    noteWithTemplateData,
    hasWritebackEnabled,
    enableVoiceNotesConsent,
    isResettingRef,
    noteContentRef,
    prevContentRef,
    prevTemplateRef,
    setNoteContent,
    setPatientConsentBlurb,
    setEditedFields,
  });

  useNoteAutoSave({
    selectedNote,
    noteContent,
    noteWithTemplateData,
    hasWritebackEnabled,
    editedFields,
    isResettingRef,
    prevContentRef,
    prevTemplateRef,
    setIsSavingNoteUpdate,
    setEditedFields,
    refetchFullNote,
  });

  const {
    saveNoteBeforeClose,
    resetNoteToOriginal,
    handleVariableValueChange,
  } = useNoteHandlers({
    selectedNote,
    noteContent,
    noteWithTemplateData,
    hasWritebackEnabled,
    enableVoiceNotesConsent,
    editedFields,
    isResettingRef,
    prevContentRef,
    prevTemplateRef,
    setNoteContent,
    setEditedFields,
    refetchFullNote,
  });

  // Computed values: variable definitions from current template fields
  const variableDefinitions = useMemo(() => {
    const fields = getCurrentFields(noteWithTemplateData);
    const definitions = extractVariableDefinitions(fields);
    if (process.env.NODE_ENV === 'development') {
      const currentTemplate = getCurrentTemplate(noteWithTemplateData);
      if (currentTemplate?.length) {
        const templateVars =
          currentTemplate[0].bodyTemplate.match(/\{\{([\w_]+)\}\}/g) || [];
        const missingVars = templateVars
          .map((v) => v.replace(/[{}]/g, ''))
          .filter((v) => !definitions.some((d) => d.varName === v));
        if (missingVars.length > 0) {
          console.warn(
            '[NoteEditor] Variables in template but not in fields:',
            missingVars,
          );
        }
      }
    }
    return definitions;
  }, [noteWithTemplateData]);

  const isNoteSavedToPMS = useMemo(
    () =>
      Boolean(
        selectedNote?.writebackStatus?.isProcessed &&
          selectedNote?.writebackStatus?.processedAt &&
          selectedNote?.writebackStatus?.errorCode === null,
      ),
    [selectedNote?.writebackStatus],
  );

  const isNoteBeingSavedToPMS = useMemo(
    () =>
      Boolean(
        selectedNote?.writebackStatus?.clinicalNoteId &&
          !selectedNote?.writebackStatus?.processedAt &&
          selectedNote?.writebackStatus?.errorCode === null,
      ),
    [selectedNote?.writebackStatus],
  );

  const hasBeenEdited = useMemo(() => {
    if (!selectedNote) return false;

    const currentTemplate = getCurrentTemplate(noteWithTemplateData);
    const isTemplateNote = Boolean(currentTemplate?.length);
    const currentContent = getNoteSummaryWithoutBlurb(
      noteContent,
      hasWritebackEnabled,
    );

    if (isTemplateNote) {
      // Get the original template from backend to check if it's been edited
      const originalTemplate =
        noteWithTemplateData?.originalTemplate?.[0]?.bodyTemplate;

      // Only check if template content differs from original (text changes)
      // Don't check for variable changes - Reset to Default only resets text edits
      return originalTemplate ? currentContent !== originalTemplate : false;
    }

    // For non-template notes, check if content differs from original
    // Use selectedNote.summary (original) instead of prevContentRef (which may be editedSummary)
    const originalSummary = selectedNote.summary || '';
    return currentContent !== originalSummary;
  }, [selectedNote, noteContent, noteWithTemplateData, hasWritebackEnabled]);

  // Functions
  const handleSetSelectedNoteId = (noteId: string | undefined) => {
    setSelectedNoteId(noteId);
  };

  useEffect(() => {
    noteContentRef.current = noteContent;
  }, [noteContent]);

  return {
    selectedNote: noteWithTemplateData,
    noteContent,
    patientConsentBlurb,
    isSavingNoteUpdate,
    hasBeenEdited,
    isNoteSavedToPMS,
    isNoteBeingSavedToPMS,
    isNoteReadOnly: isNoteSavedToPMS || isNoteBeingSavedToPMS,
    variableDefinitions,
    editedFields,
    handleSetNoteContent: setNoteContent,
    handleSetSelectedNoteId,
    handleSaveNoteBeforeClose: saveNoteBeforeClose,
    handleResetNoteToOriginal: resetNoteToOriginal,
    handleVariableValueChange,
  };
};
